import { useMemo } from 'react'
import { useI18n } from '../../i18n'
import type { DataMapFlowOperationalSummary } from '../../services/enterpriseOperationalUxApi'
import type { InformationSystemExecutiveSummary } from '../../services/enterpriseOperationalUxApi'
import styles from '../../styles/components/assets/AssetsDataMap.module.scss'

type Props = {
  flows: DataMapFlowOperationalSummary[]
  executiveBySystemId: Record<string, InformationSystemExecutiveSummary>
}

type TopoNode = {
  id: string
  label: string
  pressure?: string
  degraded?: boolean
  critical?: boolean
  x: number
  y: number
}

const NODE_W = 112
const NODE_H = 40
const GAP_X = 10
const GAP_Y = 10
const H_PAD = 12
const V_PAD = 12
const GRID_COLS = 3
const TILE_COLS = 2
const COMPONENT_GAP = 20
const MAX_LABEL = 15

function findFlowComponents(nodeIds: string[], flows: DataMapFlowOperationalSummary[]): string[][] {
  const adj = new Map<string, Set<string>>()
  for (const id of nodeIds) adj.set(id, new Set())
  for (const f of flows) {
    adj.get(f.sourceSystemId)?.add(f.targetSystemId)
    adj.get(f.targetSystemId)?.add(f.sourceSystemId)
  }
  const seen = new Set<string>()
  const components: string[][] = []
  for (const start of nodeIds) {
    if (seen.has(start)) continue
    const stack = [start]
    const comp: string[] = []
    seen.add(start)
    while (stack.length) {
      const id = stack.pop()!
      comp.push(id)
      for (const next of adj.get(id) || []) {
        if (!seen.has(next)) {
          seen.add(next)
          stack.push(next)
        }
      }
    }
    components.push(comp)
  }
  components.sort((a, b) => b.length - a.length || a[0].localeCompare(b[0]))
  return components
}

/** Prefer sources and high-degree nodes first so related IS sit near each other. */
function orderNodesForGrid(
  ids: string[],
  flows: DataMapFlowOperationalSummary[],
): string[] {
  const outDeg = new Map<string, number>()
  const inDeg = new Map<string, number>()
  for (const id of ids) {
    outDeg.set(id, 0)
    inDeg.set(id, 0)
  }
  for (const f of flows) {
    outDeg.set(f.sourceSystemId, (outDeg.get(f.sourceSystemId) || 0) + 1)
    inDeg.set(f.targetSystemId, (inDeg.get(f.targetSystemId) || 0) + 1)
  }
  return [...ids].sort((a, b) => {
    const score = (id: string) => (outDeg.get(id) || 0) * 2 + (inDeg.get(id) || 0)
    const diff = score(b) - score(a)
    if (diff !== 0) return diff
    return a.localeCompare(b)
  })
}

function layoutNodeGrid(
  nodeList: Omit<TopoNode, 'x' | 'y'>[],
  flows: DataMapFlowOperationalSummary[],
): { nodes: TopoNode[]; width: number; height: number } {
  const ids = nodeList.map((n) => n.id)
  const ordered = orderNodesForGrid(ids, flows)
  const nodeById = new Map(nodeList.map((n) => [n.id, n]))
  const cols = Math.min(GRID_COLS, Math.max(2, ordered.length <= 2 ? ordered.length : GRID_COLS))
  const rows = Math.ceil(ordered.length / cols)

  const positioned: TopoNode[] = ordered.map((id, i) => {
    const col = i % cols
    const row = Math.floor(i / cols)
    const base = nodeById.get(id)!
    return {
      ...base,
      x: H_PAD + col * (NODE_W + GAP_X),
      y: V_PAD + row * (NODE_H + GAP_Y),
    }
  })

  const width = H_PAD * 2 + cols * (NODE_W + GAP_X) - GAP_X
  const height = V_PAD * 2 + rows * (NODE_H + GAP_Y) - GAP_Y
  return { nodes: positioned, width, height }
}

function layoutAllNodes(
  nodeList: Omit<TopoNode, 'x' | 'y'>[],
  flows: DataMapFlowOperationalSummary[],
) {
  const nodeIds = nodeList.map((n) => n.id)
  const components = findFlowComponents(nodeIds, flows)
  const nodeById = new Map(nodeList.map((n) => [n.id, n]))

  if (components.length <= 1) {
    const compIds = components[0] || nodeIds
    const compSet = new Set(compIds)
    const compNodes = compIds.map((id) => nodeById.get(id)!).filter(Boolean)
    const compFlows = flows.filter(
      (f) => compSet.has(f.sourceSystemId) && compSet.has(f.targetSystemId),
    )
    const laid = layoutNodeGrid(compNodes, compFlows)
    return {
      nodes: laid.nodes,
      width: Math.max(240, laid.width),
      height: Math.max(88, laid.height),
    }
  }

  const tiles: Array<{ nodes: TopoNode[]; width: number; height: number }> = []
  for (const compIds of components) {
    const compSet = new Set(compIds)
    const compNodes = compIds.map((id) => nodeById.get(id)!).filter(Boolean)
    const compFlows = flows.filter(
      (f) => compSet.has(f.sourceSystemId) && compSet.has(f.targetSystemId),
    )
    tiles.push(layoutNodeGrid(compNodes, compFlows))
  }

  let offsetX = 0
  let offsetY = 0
  let rowMaxH = 0
  let rowMaxW = 0
  let totalW = 0
  let totalH = 0
  const positioned: TopoNode[] = []

  tiles.forEach((tile, i) => {
    const col = i % TILE_COLS
    if (col === 0 && i > 0) {
      offsetY += rowMaxH + COMPONENT_GAP
      offsetX = 0
      rowMaxH = 0
      rowMaxW = 0
    }
    for (const n of tile.nodes) {
      positioned.push({ ...n, x: n.x + offsetX, y: n.y + offsetY })
    }
    offsetX += tile.width + COMPONENT_GAP
    rowMaxH = Math.max(rowMaxH, tile.height)
    rowMaxW = Math.max(rowMaxW, offsetX - COMPONENT_GAP)
    totalW = Math.max(totalW, rowMaxW)
    totalH = offsetY + rowMaxH
  })

  return {
    nodes: positioned,
    width: Math.max(240, totalW),
    height: Math.max(88, totalH),
  }
}

/** Short edge: horizontal when possible, otherwise a tight cubic curve (no long elbows). */
function edgePath(s: TopoNode, tg: TopoNode) {
  const x1 = s.x + NODE_W
  const y1 = s.y + NODE_H / 2
  const x2 = tg.x
  const y2 = tg.y + NODE_H / 2

  if (x2 > x1 + 6 && Math.abs(y1 - y2) < NODE_H * 0.6) {
    return `M ${x1} ${y1} L ${x2} ${y2}`
  }
  if (x1 > x2 + 6 && Math.abs(y1 - y2) < NODE_H * 0.6) {
    return `M ${s.x} ${y1} L ${x2 + NODE_W} ${y2}`
  }

  const dx = (x2 - x1) * 0.35
  const c1x = x1 + dx
  const c2x = x2 - dx
  return `M ${x1} ${y1} C ${c1x} ${y1}, ${c2x} ${y2}, ${x2} ${y2}`
}

type DedupedEdge = {
  id: string
  source: string
  target: string
  degraded: boolean
  propagatesRisk: boolean
  readinessImpact: boolean
}

function dedupeEdges(flows: DataMapFlowOperationalSummary[]): DedupedEdge[] {
  const byPair = new Map<string, DedupedEdge>()
  for (const f of flows) {
    const key = `${f.sourceSystemId}\0${f.targetSystemId}`
    const next: DedupedEdge = {
      id: f.id,
      source: f.sourceSystemId,
      target: f.targetSystemId,
      degraded: f.operationalState.degraded,
      propagatesRisk: f.blastRadius.blastRadiusContribution > 0,
      readinessImpact: f.runtime.driftDetected,
    }
    const prev = byPair.get(key)
    if (!prev) {
      byPair.set(key, next)
      continue
    }
    byPair.set(key, {
      ...prev,
      id: next.degraded ? next.id : prev.id,
      degraded: prev.degraded || next.degraded,
      propagatesRisk: prev.propagatesRisk || next.propagatesRisk,
      readinessImpact: prev.readinessImpact || next.readinessImpact,
    })
  }
  return [...byPair.values()]
}

export default function DataMapTopologyMiniGraph({ flows, executiveBySystemId }: Props) {
  const { t } = useI18n()

  const { nodes, edges, width, height } = useMemo(() => {
    const nodeMap = new Map<string, Omit<TopoNode, 'x' | 'y'>>()
    for (const f of flows) {
      if (!nodeMap.has(f.sourceSystemId)) {
        const ex = executiveBySystemId[f.sourceSystemId]
        nodeMap.set(f.sourceSystemId, {
          id: f.sourceSystemId,
          label: f.sourceSystemName,
          pressure: ex?.pressure,
          degraded: (ex?.degradedControlCount || 0) > 0,
          critical: f.dataContext.criticalData,
        })
      }
      if (!nodeMap.has(f.targetSystemId)) {
        const ex = executiveBySystemId[f.targetSystemId]
        nodeMap.set(f.targetSystemId, {
          id: f.targetSystemId,
          label: f.targetSystemName,
          pressure: ex?.pressure,
          degraded: (ex?.degradedControlCount || 0) > 0,
        })
      }
    }
    const nodeList = [...nodeMap.values()]
    const { nodes: positioned, width: w, height: h } = layoutAllNodes(nodeList, flows)
    return { nodes: positioned, edges: dedupeEdges(flows), width: w, height: h }
  }, [flows, executiveBySystemId])

  if (!flows.length) {
    return (
      <p className={styles.widgetEmpty}>
        {t('enterpriseOperationalUx.dataMap.noTopologyGraph', 'No data flows to visualize operational topology.')}
      </p>
    )
  }

  return (
    <div className={styles.topologyGraphWrap}>
      <p className={styles.topologyGraphLegend}>
        {t('enterpriseOperationalUx.dataMap.graphLegend', 'Nodes: pressure · degraded · critical data. Edges: risk propagation.')}
      </p>
      <svg
        className={styles.topologyGraphSvg}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={t('enterpriseOperationalUx.dataMap.operationalTopology', 'Operational topology')}
      >
        <defs>
          <marker
            id="topology-arrow-sm"
            markerWidth="5"
            markerHeight="5"
            refX="4.5"
            refY="2.5"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M0,0 L5,2.5 L0,5 z" fill="currentColor" />
          </marker>
        </defs>
        <g className={styles.graphEdgesLayer}>
          {edges.map((e) => {
            const s = nodes.find((n) => n.id === e.source)
            const tg = nodes.find((n) => n.id === e.target)
            if (!s || !tg) return null
            const cls = [
              styles.graphEdge,
              e.degraded ? styles.graphEdgeDegraded : '',
              e.propagatesRisk ? styles.graphEdgePropagates : '',
              e.readinessImpact ? styles.graphEdgeReadiness : '',
            ]
              .filter(Boolean)
              .join(' ')
            const showArrow = e.degraded || e.propagatesRisk
            return (
              <path
                key={`${e.source}-${e.target}`}
                d={edgePath(s, tg)}
                className={cls}
                markerEnd={showArrow ? 'url(#topology-arrow-sm)' : undefined}
              />
            )
          })}
        </g>
        <g className={styles.graphNodesLayer}>
          {nodes.map((n) => {
            const glow =
              n.pressure === 'CRITICAL' || n.pressure === 'HIGH'
                ? styles.graphNodePressure
                : n.degraded
                  ? styles.graphNodeDegraded
                  : n.critical
                    ? styles.graphNodeCritical
                    : ''
            const label =
              n.label.length > MAX_LABEL ? `${n.label.slice(0, MAX_LABEL - 1)}…` : n.label
            return (
              <g key={n.id} transform={`translate(${n.x},${n.y})`}>
                <rect width={NODE_W} height={NODE_H} rx={8} className={`${styles.graphNode} ${glow}`.trim()} />
                <text x={NODE_W / 2} y={NODE_H / 2 + 4} textAnchor="middle" className={styles.graphNodeLabel}>
                  {label}
                </text>
              </g>
            )
          })}
        </g>
      </svg>
    </div>
  )
}
